import {useTranslation} from 'react-i18next';
import {Navigate, useLocation, useParams} from 'react-router-dom';

import {PageContent, PageLayout} from '@/layouts';

import {useTrustDetailsPackageQuery} from '../features/trust-accounts/activation/hooks';
import {useTrustAccountBalanceQuery, useTrustAccountQuery} from '../features/trust-accounts/hooks';
import {
  getTrustAccountActivationPath,
  getTrustTypeLabel,
  getTrustTypeSubtitle,
  isTrustInActivationPhase
} from '../features/trust-accounts/utils';
import {TrustAccountView} from '../features/trust-accounts/view/components';
import {QuickActionsFabSkeleton} from '../features/trust-accounts/view/components/QuickActionsFab/QuickActionsFab.skeleton';
import {ActivityTabContentSkeleton} from '../features/trust-accounts/view/components/tabs/activity/ActivityTabContent.skeleton';
import {BeneficiariesTabContentSkeleton} from '../features/trust-accounts/view/components/tabs/beneficiaries/BeneficiariesTabContent.skeleton';
import {DocumentsTabContentSkeleton} from '../features/trust-accounts/view/components/tabs/documents/DocumentsTabContent.skeleton';
import {InstructionsTabContentSkeleton} from '../features/trust-accounts/view/components/tabs/instructions/InstructionsTabContent.skeleton';
import {PayoutsTabContentSkeleton} from '../features/trust-accounts/view/components/tabs/payouts/PayoutsTabContent.skeleton';
import {TransactionsTabContentSkeleton} from '../features/trust-accounts/view/components/tabs/transactions/TransactionsTabContent.skeleton';
import {TrustAccountViewHeaderSkeleton} from '../features/trust-accounts/view/components/TrustAccountViewHeader.skeleton';
import {useTrustAccountViewTab} from '../features/trust-accounts/view/hooks';

import type {TrustAccountViewTab} from '../features/trust-accounts/view/utils';
import type {FC} from 'react';

function getTabSkeleton(tab: TrustAccountViewTab) {
  switch (tab) {
    case 'overview':
      return <ActivityTabContentSkeleton />;
    case 'documents':
      return <DocumentsTabContentSkeleton />;
    case 'beneficiaries':
      return <BeneficiariesTabContentSkeleton />;
    case 'transactions':
      return <TransactionsTabContentSkeleton />;
    case 'payouts':
      return <PayoutsTabContentSkeleton />;
    case 'instructions':
      return <InstructionsTabContentSkeleton />;
    default:
      return null;
  }
}

export const TrustAccountViewPage: FC = () => {
  const {id} = useParams<{id: string}>();
  const {search} = useLocation();
  const {t} = useTranslation('trustAccounts');

  const {data: account, isLoading, error, refetch} = useTrustAccountQuery(id ?? '');
  const isActiveTrust = !!account && !isTrustInActivationPhase(account.trustAccount.trustStatus);
  const {data: trustDetailsPackage} = useTrustDetailsPackageQuery(id ?? '', {
    enabled: isActiveTrust
  });
  const {
    data: balanceData,
    dataUpdatedAt: balanceUpdatedAt,
    isLoading: isBalanceLoading
  } = useTrustAccountBalanceQuery(id ?? '', {
    enabled: isActiveTrust
  });

  const {activeTab, handleTabChange} = useTrustAccountViewTab();

  if (isLoading || !account)
    return (
      <PageLayout error={error} refetch={() => void refetch()}>
        <TrustAccountViewHeaderSkeleton activeTab={activeTab} onTabChange={handleTabChange} />
        <PageContent>{getTabSkeleton(activeTab)}</PageContent>
        <QuickActionsFabSkeleton />
      </PageLayout>
    );

  const {trustAccount} = account;

  if (!isActiveTrust) {
    // Keeps `?share=` so the share panel deep link still opens on the activation page.
    return (
      <Navigate to={{pathname: getTrustAccountActivationPath(trustAccount.id), search}} replace />
    );
  }

  const purpose = getTrustTypeLabel(trustAccount.trustPurpose, t);
  // Same helper the list table's type column uses, so both read identically.
  const transactionType = getTrustTypeSubtitle(trustAccount.generalSaleTransactionType, t);
  const sellers = trustDetailsPackage?.data.sellers ?? [];

  return (
    <TrustAccountView
      id={id ?? ''}
      name={trustAccount.name}
      refId={trustAccount.refId}
      status={trustAccount.trustStatus}
      balance={balanceData?.data ?? null}
      balanceUpdatedAt={balanceUpdatedAt}
      isBalanceLoading={isBalanceLoading}
      sellers={sellers}
      purpose={purpose}
      transactionType={transactionType}
      account={trustAccount}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      error={error}
      refetch={() => void refetch()}
    />
  );
};
